import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import HTMLFlipBook from "react-pageflip";
import LibraryComicPdfPager from "./LibraryComicPdfPager";

const FlipPage = forwardRef(function FlipPage({ children, className = "" }, ref) {
  return (
    <div className={`lread-flip-page ${className}`.trim()} ref={ref}>
      {children}
    </div>
  );
});

function FlipToolbar({ bookRef, pageLabel, syncPageIndex }) {
  const flip = useCallback(
    (fn) => {
      const api = bookRef.current?.pageFlip?.();
      if (!api) return;
      try {
        fn(api);
        window.requestAnimationFrame(() => syncPageIndex?.());
      } catch {
        /* noop */
      }
    },
    [bookRef, syncPageIndex]
  );

  return (
    <div className="lread-flip-toolbar" role="toolbar" aria-label="Flipbook controls">
      <button type="button" className="lread-flip-btn" onClick={() => flip((p) => p.flipPrev())}>
        ← Prev
      </button>
      <span className="lread-flip-toolbar__meta">{pageLabel}</span>
      <button type="button" className="lread-flip-btn" onClick={() => flip((p) => p.flipNext())}>
        Next →
      </button>
    </div>
  );
}

/**
 * Comic reader: page-turn flipbook for image pages; single-page react-pdf pager for PDF (reliable layout).
 */
export default function LibraryComicFlipbook({
  pageImages = [],
  pdfUrl = null,
  title = "Comic",
  onPageChange,
  variant = "inline",
  startImagePage = 0,
  pdfInitialPageIndex = 0,
}) {
  const wrapRef = useRef(null);
  const bookRef = useRef(null);
  const [dims, setDims] = useState({ w: 400, h: 648, containerW: 520 });
  const [pageIndex, setPageIndex] = useState(0);

  const portraitMinWidth = Math.max(220, Math.ceil(dims.containerW / 2) + 48);
  const bookMinWidth = variant === "overlay" ? Math.max(240, Math.min(Math.floor(dims.containerW * 0.3), 520)) : portraitMinWidth;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const apply = () => {
      const rect = el.getBoundingClientRect();
      const cw = Math.max(240, rect.width);
      const reserveY = variant === "overlay" ? 72 : 120;
      let ch = Math.max(220, rect.height - reserveY);
      if (variant === "overlay" && (!Number.isFinite(rect.height) || rect.height < 180)) {
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        ch = Math.max(380, Math.floor(vh * 0.68) - reserveY);
      }
      const ratio = 1.58;
      const bookCap = variant === "overlay" ? Math.min(1400, Math.floor(cw - 32)) : 620;
      let w = Math.min(bookCap, Math.max(260, cw - 24));
      let h = Math.round(w * ratio);
      if (h > ch) {
        h = Math.max(280, Math.floor(ch));
        w = Math.round(h / ratio);
      }
      if (w > cw - 24) {
        w = Math.max(220, cw - 24);
        h = Math.round(w * ratio);
      }
      setDims({ w, h, containerW: cw });
    };

    apply();
    const ro = new ResizeObserver(() => {
      apply();
      requestAnimationFrame(apply);
    });
    ro.observe(el);
    window.addEventListener("resize", apply);
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [variant]);

  const syncPageIndex = useCallback(() => {
    const api = bookRef.current?.pageFlip?.();
    const idx = api?.getCurrentPageIndex?.();
    if (typeof idx === "number" && !Number.isNaN(idx)) setPageIndex(idx);
  }, []);

  const pages = Array.isArray(pageImages) ? pageImages : [];
  const useImages = pages.length > 0;

  const imgStartRef = useRef(null);
  if (imgStartRef.current === null && pages.length > 0) {
    const raw = Math.floor(Number(startImagePage) || 0);
    imgStartRef.current = Math.max(0, Math.min(raw, pages.length - 1));
  }
  const imgStart = imgStartRef.current ?? 0;

  const flipBy = useCallback(
    (fn) => {
      const api = bookRef.current?.pageFlip?.();
      if (!api) return;
      try {
        fn(api);
        window.requestAnimationFrame(() => {
          const idx = api?.getCurrentPageIndex?.();
          if (typeof idx === "number" && !Number.isNaN(idx)) setPageIndex(idx);
        });
      } catch {
        /* noop */
      }
    },
    []
  );

  useEffect(() => {
    if (!useImages || !pages.length) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        flipBy((p) => p.flipPrev());
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        flipBy((p) => p.flipNext());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [useImages, pages.length, flipBy]);

  const totalFlipPages = useImages ? pages.length : 0;
  const safeIdx = useImages
    ? Math.max(0, Math.min(pageIndex, pages.length - 1))
    : 0;
  const filePageNum = useImages ? pages[safeIdx]?.pageNumber : null;
  const pageLabel =
    totalFlipPages > 0
      ? useImages &&
          filePageNum != null &&
          String(filePageNum).trim() !== "" &&
          Number.isFinite(Number(filePageNum))
        ? `View ${safeIdx + 1} of ${totalFlipPages} · file page ${filePageNum}`
        : `Page ${safeIdx + 1} of ${totalFlipPages}`
      : "Loading…";

  useEffect(() => {
    if (typeof onPageChange !== "function" || !useImages || !pages.length) return;
    const pn = filePageNum != null && Number.isFinite(Number(filePageNum)) ? Number(filePageNum) : safeIdx + 1;
    onPageChange({ index: safeIdx, pageNumber: pn, total: pages.length, mode: "image" });
  }, [safeIdx, pages, useImages, onPageChange, filePageNum]);

  if (pdfUrl && !useImages) {
    return (
      <div className={`lread-flipbook lread-flipbook--stretch${variant === "overlay" ? " lread-flipbook--overlay" : ""}`} ref={wrapRef}>
        <LibraryComicPdfPager
          pdfUrl={pdfUrl}
          title={title}
          onPageChange={onPageChange}
          variant={variant}
          initialPageIndex={pdfInitialPageIndex}
        />
      </div>
    );
  }

  if (useImages) {
    return (
      <div className={`lread-flipbook lread-flipbook--stretch${variant === "overlay" ? " lread-flipbook--overlay" : ""}`} ref={wrapRef}>
        <p className="lread-flipbook__hint">
          Flipbook — drag a corner or edge to turn pages. {title}
        </p>
        <div className="lread-flipbook__shell lread-flipbook__shell--flex">
          <HTMLFlipBook
            key={
              variant === "overlay"
                ? `img-ov-${imgStart}-${pages.length}-${dims.containerW}`
                : `img-${dims.containerW}-${bookMinWidth}-${pages.length}`
            }
            ref={bookRef}
            width={dims.w}
            height={dims.h}
            size="stretch"
            minWidth={bookMinWidth}
            maxWidth={variant === "overlay" ? Math.min(1400, Math.max(360, dims.containerW - 24)) : 620}
            minHeight={Math.min(420, dims.h)}
            maxHeight={Math.max(dims.h, 320)}
            showPageCorners
            mobileScrollSupport
            usePortrait
            showCover={false}
            maxShadowOpacity={0.45}
            className="lread-flipbook__book"
            startPage={imgStart}
            onFlip={syncPageIndex}
          >
            {pages.map((pg) => (
              <FlipPage key={pg.id}>
                <div
                  className="lread-flip-page__inner"
                  style={{ width: "100%", height: "100%", boxSizing: "border-box" }}
                >
                  <img
                    className="lread-flip-page__img"
                    src={pg.readerUrl}
                    alt={pg.displayName || (pg.pageNumber != null ? `Page ${pg.pageNumber}` : "Comic page")}
                    draggable={false}
                  />
                  {pg.pageNumber != null ? (
                    <span className="lread-flip-page__num">Page {pg.pageNumber}</span>
                  ) : null}
                </div>
              </FlipPage>
            ))}
          </HTMLFlipBook>
        </div>
        <FlipToolbar bookRef={bookRef} pageLabel={pageLabel} syncPageIndex={syncPageIndex} />
      </div>
    );
  }

  return null;
}
